import React, { useState, useEffect } from 'react';
import { SkillsMapper } from '../utils/skillsMapper';

const SkillsAuditView = ({ skills, capabilities, people }) => {
  const [skillsMapper, setSkillsMapper] = useState(null);
  const [validationIssues, setValidationIssues] = useState([]);
  const [unusedSkills, setUnusedSkills] = useState([]);
  const [usageReport, setUsageReport] = useState(null);
  const [activeTab, setActiveTab] = useState('validation');

  useEffect(() => {
    if (skills && capabilities && people) {
      const mapper = new SkillsMapper(skills, capabilities, people);
      setSkillsMapper(mapper);
      
      // Run validation
      const issues = mapper.validateAll();
      setValidationIssues(issues);
      
      // Get unused skills
      const unused = mapper.getUnusedSkills();
      setUnusedSkills(unused);
      
      // Generate usage report
      const report = mapper.generateSkillUsageReport();
      setUsageReport(report);
    }
  }, [skills, capabilities, people]);

  const getIssueIcon = (type) => {
    switch (type) {
      case 'missing_skill':
        return '⚠️';
      default:
        return '❌';
    }
  };

  const getIssueColor = (type) => {
    switch (type) {
      case 'missing_skill':
        return 'border-yellow-200 bg-yellow-50';
      default:
        return 'border-red-200 bg-red-50';
    }
  };

  const renderValidationTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Validation Summary</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-900">{skills?.length || 0}</div>
            <div className="text-sm text-blue-700">Total Skills</div>
          </div>
          
          <div className={`border rounded-lg p-4 ${validationIssues.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <div className={`text-2xl font-bold ${validationIssues.length > 0 ? 'text-red-900' : 'text-green-900'}`}>
              {validationIssues.length}
            </div>
            <div className={`text-sm ${validationIssues.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
              Validation Issues
            </div>
          </div>
          
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-900">{unusedSkills.length}</div>
            <div className="text-sm text-orange-700">Unused Skills</div>
          </div>
        </div>

        {validationIssues.length > 0 ? (
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Issues Found:</h4>
            <div className="space-y-3">
              {validationIssues.map((issue, index) => (
                <div key={index} className={`border rounded-lg p-4 ${getIssueColor(issue.type)}`}>
                  <div className="flex items-start space-x-3">
                    <span className="text-xl">{getIssueIcon(issue.type)}</span>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {issue.message}
                      </div>
                      {issue.capability && (
                        <div className="text-xs text-gray-600 mt-1">
                          Capability: {issue.capability}
                        </div>
                      )}
                      {issue.person && (
                        <div className="text-xs text-gray-600 mt-1">
                          Person: {issue.person}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">✅</div>
            <div className="text-lg font-medium text-green-900">All skills are valid!</div>
            <div className="text-sm text-green-700">No validation issues found.</div>
          </div>
        )}
      </div>

      {unusedSkills.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Unused Skills</h3>
          <p className="text-sm text-gray-600 mb-4">
            These skills are not used by any capabilities or people. Consider removing them or adding them to relevant capabilities.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {unusedSkills.map(skill => (
              <div key={skill.id} className="border border-orange-200 bg-orange-50 rounded-lg p-3">
                <div className="font-medium text-sm text-orange-900">{skill.name}</div>
                <div className="text-xs text-orange-700 mt-1">{skill.category}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderUsageTab = () => (
    <div className="space-y-6">
      {usageReport && (
        <>
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Overview</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Skills by Category</h4>
                <div className="space-y-2">
                  {Object.entries(usageReport.skillsByCategory).map(([category, count]) => (
                    <div key={category} className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">{category}</span>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Usage Statistics</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Total Skills</span>
                    <span className="text-sm font-medium text-gray-900">{usageReport.totalSkills}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Used Skills</span>
                    <span className="text-sm font-medium text-green-700">{usageReport.usedSkills}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Unused Skills</span>
                    <span className="text-sm font-medium text-orange-700">{usageReport.unusedSkills}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Skills in Capabilities</h3>
              <div className="space-y-3">
                {usageReport.topSkillsInCapabilities.map((item, index) => (
                  <div key={item.skillId} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="text-sm font-medium text-gray-500">#{index + 1}</div>
                      <div className="text-sm text-gray-900">{item.skillName}</div>
                    </div>
                    <div className="text-sm font-medium text-blue-600">{item.count}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Skills in People</h3>
              <div className="space-y-3">
                {usageReport.topSkillsInPeople.map((item, index) => (
                  <div key={item.skillId} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="text-sm font-medium text-gray-500">#{index + 1}</div>
                      <div className="text-sm text-gray-900">{item.skillName}</div>
                    </div>
                    <div className="text-sm font-medium text-green-600">{item.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (!skillsMapper) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center">
          <div className="text-gray-500">Loading skills audit...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Skills Audit & Validation</h2>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('validation')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'validation'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Validation Issues
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'usage'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Usage Analysis
          </button>
        </nav>
      </div>

      <div className="p-6">
        {activeTab === 'validation' && renderValidationTab()}
        {activeTab === 'usage' && renderUsageTab()}
      </div>
    </div>
  );
};

export default SkillsAuditView;